<!-- eslint-disable vue/no-template-key -->
<template>
  <div id="paymentMethodsId" class="payment-methods-container">
    <div class="headline-m">
      วิธีการชำระเงิน
    </div>

    <slot name="payment-methods-header"></slot>

    <div class="parent-container">
      <template v-for="group in paymentGroups">
        <div
          :key="group.key"
          :class="[
            'item-container',
            group.key,
            { '-is-selected': group.selected },
          ]"
        >
          <div
            class="item"
            :class="{
              '-is-unavailable': group.unavailable,
            }"
            @click="
              onSelectPaymentMethod(
                onFindPaymentMethodFirstAvailable(group.items),
              )
            "
          >
            <span class="checkmark"></span>
            <div>
              {{ group.title }}
            </div>
            <div>
              <span
                v-if="group.unavailable"
                class="badge-unavailable"
              >
                ไม่พร้อมใช้งาน
              </span>
            </div>
          </div>

          <div
            v-if="group.selected && group.expanded"
            class="item-desc"
          >
            <template v-for="item in group.items">
              <PaymentMethodItem
                :key="item.id"
                data-pptr="payment_type_id"
                :hasError="isErrorRequirePayment"
                :name="item.title"
                :label="item.short_description"
                :image="item.icon || iconBank[item.id]"
                :selected="paymentId === item.id"
                :disabled="item.disabled"
                :isLessThanMinAmount="item.isLessThanMinAmount"
                :lessThanMinAmount="item.min_amount"
                @click="onSelectPaymentMethod(item)"
              />

              <div
                v-if="
                  paymentId === item.id &&
                    isHaveDropdown(item.dropdown)
                "
                :key="`items-${item.id}`"
                class="installment-plan-container"
              >
                <label class="text-sm">
                  เลือกวิธีการผ่อนชำระ
                  <span class="text-red">*</span>
                </label>
                <v-select
                  v-model="installmentPlan"
                  :dense="true"
                  :items="handleDropdown(item.dropdown)"
                  color="primary"
                  class="text-sm"
                  background-color="white"
                  item-text="terms"
                  item-value="item"
                  placeholder="เลือกวิธีการผ่อนชำระ"
                  outlined
                  hide-details
                  return-object
                  @change="handleUpdateInstallmentPlan"
                >
                  <template slot="selection" slot-scope="data">
                    ผ่อนชำระ {{ data.item.terms }} เดือน ดอกเบี้ย
                    {{ Number(data.item.interest) }}%
                  </template>
                  <template slot="item" slot-scope="data">
                    ผ่อนชำระ {{ data.item.terms }} เดือน ดอกเบี้ย
                    {{ Number(data.item.interest) }}%
                  </template>
                </v-select>
              </div>
            </template>

            <div class="payment-method-hint">
              <v-icon size="16">mdi-information</v-icon>
              <span>
                การชำระเงินผ่าน {{ paymentTitle }}
                กรุณาตรวจสอบวงเงินคงเหลือ
                ของคุณก่อนทำการยืนยันการสั่งซื้อ
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import iconBank from '@/config/iconBank'
import { STOCK_CARD_TYPE } from '@/config/stockCard'

export default {
  name: 'PaymentMethods',

  components: {
    PaymentMethodItem: () =>
      import('@/components/payments/base/PaymentMethodItem.vue'),
  },

  props: {
    type: {
      type: String,
      default: 'default', // 'default' | 'trade-in' | 'vip'
    },
    finalPrice: {
      type: Number,
      default: 0,
    },
    isDeposit: {
      type: Boolean,
      default: false,
    },
    isErrorRequirePayment: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      iconBank,
      installmentPlan: null,
      paymentOptions: {
        payment8: {
          term: null, // bay
          cardType: 'creditcard', // bay
        },
        payment13: {
          term: null, // bay
          cardType: 'loancard', // bay first choice
        },
      },
    }
  },

  computed: {
    ...mapState({
      campaign: state => state.campaign.current,
      paymentTypeData: state => state.paymentType.data,
      paymentId: state => state.paymentType.paymentId,
      order: state => state.order.data,
      changePaymentType: state =>
        state.paymentType.techswop.changePaymentType?.data,
    }),

    uuid() {
      return this.$route.params.uid || this.$route.params.orderId
    },

    campaignId() {
      return this.campaign?.data?.id ?? ''
    },

    decoratedPaymentTypeData() {
      return (this.paymentTypeData ?? []).map(i => {
        if (!i) return i

        const disabled = this.isDisabledPaymentMethod(i.min_amount)

        return { ...i, disabled, isLessThanMinAmount: disabled }
      })
    },

    depositList() {
      return this.decoratedPaymentTypeData.filter(
        i => i?.type === 'deposit',
      )
    },

    payAtStoreList() {
      return this.decoratedPaymentTypeData.filter(
        i => i?.type === 'pay-at-store',
      )
    },

    fullPaymentList() {
      return this.decoratedPaymentTypeData.filter(
        i => i?.type === 'full-payment',
      )
    },

    installmentList() {
      return this.decoratedPaymentTypeData.filter(
        i => i?.type === 'installment',
      )
    },

    isShowPayAtStoreMethod() {
      const { t } = this.$route.query

      return (
        this.payAtStoreList.length > 0 &&
        (t === STOCK_CARD_TYPE.STORE || t === STOCK_CARD_TYPE.QR)
      )
    },

    paymentGroups() {
      const groups = this.isDeposit
        ? [
            {
              key: 'deposit',
              title: 'ชำระเงินแบบมัดจำ',
              items: this.depositList,
              expanded: true,
            },
          ]
        : this.isShowPayAtStoreMethod
        ? [
            {
              key: 'pay-at-store',
              title: 'ชำระเงินที่สาขา',
              items: this.payAtStoreList,
              expanded: this.payAtStoreList.length > 1,
            },
          ]
        : [
            {
              key: 'full-payment',
              title: 'ชำระเงินแบบเต็มจำนวน',
              items: this.fullPaymentList,
              expanded: true,
            },
            {
              key: 'installment',
              title: 'ผ่อนชำระด้วยบัตรเครดิต',
              items: this.installmentList,
              expanded: true,
            },
          ]

      return groups
        .filter(group => group.items.length > 0)
        .map(group => ({
          ...group,
          selected: group.items.some(
            item => item.id === this.paymentId,
          ),
          unavailable: group.items.every(item => item.disabled),
        }))
    },

    paymentTitle() {
      const payment = this.paymentTypeData?.find(
        i => i.id === this.paymentId,
      )
      return payment?.title || ''
    },
  },

  watch: {
    isDeposit() {
      this.autoSelectPayment()
    },
  },

  async mounted() {
    await this.getPaymentMethods()

    const defaultPaymentId = this.order?.payment_type_id
    const selectedPayment = this.decoratedPaymentTypeData.find(
      item => item?.id === defaultPaymentId,
    )

    // Restore the order's already-selected payment method, if it's still selectable
    if (
      defaultPaymentId &&
      selectedPayment &&
      !selectedPayment.disabled
    ) {
      this.$store.commit('paymentType/setPaymentId', defaultPaymentId)

      // If the order has an installment plan, restore it as well
      const defaultInstallmentPlan = {
        terms: this.order.payment_selected?.term || null,
        interest: this.order.payment_selected?.interest || null,
        card_type: this.order.payment_selected?.card_type || null,
      }

      if (defaultInstallmentPlan.terms) {
        this.$store.commit(
          'paymentType/setPaymentInstallmentPlan',
          defaultInstallmentPlan,
        )
        this.installmentPlan = defaultInstallmentPlan
      }
    } else {
      await this.autoSelectPayment()
    }
  },

  methods: {
    async getPaymentMethods() {
      switch (this.type) {
        case 'vip':
          // Do nothing, VIP payment methods are handled elsewhere
          break
        case 'trade-in':
          await this.getTradeInPaymentMethods()
          break
        default:
          await this.getDefaultPaymentMethods()
          break
      }
    },

    async getDefaultPaymentMethods() {
      await this.$store.dispatch(
        'paymentType/getPaymentType',
        this.campaignId,
      )
    },

    async getVipPaymentMethods() {
      if (!this.uuid) return

      await this.$store.dispatch(
        'paymentType/getVipPaymentMethods',
        this.uuid,
      )
    },

    async getTradeInPaymentMethods() {
      if (!this.uuid) return

      await this.$store.dispatch(
        'paymentType/getTradeInPaymentMethods',
        {
          order_uuid: this.uuid,
          tradein_type: 'tradein',
        },
      )
    },

    onFindPaymentMethodFirstAvailable(event) {
      return event.find(i => !i.disabled) ?? null
    },

    autoSelectPayment() {
      if (this.isDeposit) {
        this.onSelectPaymentMethod(
          this.onFindPaymentMethodFirstAvailable(this.depositList),
        )
      } else if (this.isShowPayAtStoreMethod) {
        this.onSelectPaymentMethod(
          this.onFindPaymentMethodFirstAvailable(this.payAtStoreList),
        )
      } else {
        this.onSelectPaymentMethod(
          this.onFindPaymentMethodFirstAvailable(
            this.fullPaymentList,
          ),
        )
      }
    },

    onSelectPaymentMethod(event) {
      if (!event || event.disabled) return

      this.$store.commit('paymentType/setPaymentId', event.id)
      this.$emit('onSelectPaymentMethod', event.id)

      const plans = this.handleDropdown(event.dropdown)
      const plan = {
        ...plans?.[0],
        card_type:
          this.paymentOptions?.[`payment${this.paymentId}`]
            ?.cardType ?? null,
      }

      if (plans?.length > 0) {
        this.$store.commit(
          'paymentType/setPaymentInstallmentPlan',
          plan,
        )
        this.installmentPlan = plan
      } else {
        this.$store.commit(
          'paymentType/setPaymentInstallmentPlan',
          null,
        )
        this.installmentPlan = null
      }
    },

    // change payment method
    async handleChangingPaymentMethod({
      tradeInType = null,
      vouchers = [],
    } = {}) {
      if (!this.paymentId) return null

      try {
        const payment_selected = {
          term: this.installmentPlan?.terms ?? null,
          interest: this.installmentPlan?.interest ?? 0,
          card_type: this.installmentPlan?.card_type ?? null,
        }

        const payload = {
          order_uuid: this.uuid,
          tradein_type: tradeInType,
          payment_type_id: this.paymentId,
          vouchers: vouchers.map(voucher => voucher.code),
          payment_selected,
        }

        await this.$store.dispatch(
          'paymentType/changeTradeInPaymentMethod',
          payload,
        )

        return this.changePaymentType
      } catch {
        return null
      }
    },

    handleUpdateInstallmentPlan(value) {
      this.$store.commit('paymentType/setPaymentInstallmentPlan', {
        ...value,
        card_type:
          this.paymentOptions?.[`payment${this.paymentId}`]
            ?.cardType ?? null,
      })
    },

    handleDropdown(event) {
      if (typeof event === 'object') {
        return event
      } else if (typeof event === 'string') {
        return JSON.parse(event)
      }
      return []
    },

    isHaveDropdown(event) {
      const data = this.handleDropdown(event)

      if (!data) return false

      return data.length > 0
    },

    isDisabledPaymentMethod(min_amount) {
      return this.finalPrice < min_amount
    },
  },
}
</script>

<style lang="stylus" scoped>
.payment-methods-container {
    display flex
    flex-direction column
    gap 24px

    .parent-container {
        .item-container {
            &:not(.-is-selected):not(:last-of-type) {
                .item {
                    border-bottom: 0;
                }
            }

            &:first-of-type {
                .item {
                    border-radius: 8px 8px 0 0;
                }
            }

            &:last-child:not(:first-child) {
                > .item {
                    border-radius: 0 0 8px 8px;
                }

                &.-is-selected {
                    > .item {
                        border-radius: 0;
                    }

                    .item-desc {
                        border-radius: 0 0 8px 8px;
                    }
                }
            }

            &:first-of-type:last-child {
                .item-desc {
                    border-radius: 0 0 8px 8px;
                }
            }

            &.-is-selected {
                .item {
                    background-color: $color-fill-radio-button;
                    border: 1.2px solid $color-bnn;

                    .checkmark {
                        border: 5.5px solid $color-bnn;
                    }
                }
            }

            &:not(:last-child):not(:first-child) {
                .item {
                    border-top: 0;
                }
            }

            &.-is-selected:not(:last-child) {
                .item-desc {
                    border-bottom: none;
                    border-top: none;
                }
            }

            &.pay-at-store {
                .item {
                    border-radius: 8px;
                }
            }
        }
    }

    .item {
        display: flex;
        align-items: center;
        gap: 11px;
        padding: 11px;

        border: 1.2px solid $color-dapp-gray-3;
        cursor: pointer;

        &.-is-unavailable {
            opacity: 0.4;
            cursor: not-allowed;
            pointer-events: none;
        }

        .item-label {
            flex: 1;

            > .label {
                font-weight: 500;
                color: rgba(#000000, 0.70);
            }

            > .description-s {
                font-size: 12px;
                font-weight: 600;
                line-height: 1.3;
                color: rgba(#000000, 0.45);
            }
        }

        .item-icon {
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;

            img {
                object-fit: contain;
                width: 100%;
                height: 100%;
            }
        }

        .checkmark {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            border: 1px solid #d2d2d7;
            transition: border .1s;
        }
    }

    .item-desc {
        background-color: rgba(0, 0, 0, 0.043)
        border: 1px solid rgb(222, 222, 222)
        box-sizing: border-box
        color: rgb(0, 0, 0)
        display: block
        font-size: 14px
        font-weight: 400
        letter-spacing: normal
        line-height: 21px
        padding: 17px

        width: 100%
        display: grid
        grid-template-columns: repeat(2, 1fr)
        gap: 12px 16px

        @media screen and (min-width: 1024px) {
            grid-template-columns: repeat(3, 1fr)
        }
    }

    .installment-plan-container {
        grid-column: span 2
        order: 1

        @media screen and (min-width: 1024px) {
            grid-column: span 3
        }
    }

	.payment-method-hint {
        grid-column span 2
        order: 2

        display: flex
        align-items: flex-start
        gap: 8px

        background-color: rgba($color-red, 0.1)
        border-radius: 8px
        padding: 8px 12px

        font-size: 14px
        line-height: 1.5
        font-weight: 500
        color: $color-red

        @media screen and (min-width: 1024px) {
            grid-column span 3
        }

        .v-icon {
            color: $color-red
            flex-shrink: 0
        }
    }
}

.badge-unavailable {
	font-size: 12px;
	font-weight: 600;
	background-color: rgba(#000000, 0.10);
	color: rgba(#000000, 0.65);
	border-radius: 8px;
	padding: 2px 8px;
}

.alert-warning {
	font-size: 14px
	background-color: rgba($color-bnn-red, 0.15)
	color: $color-bnn-red
	padding: 8px 16px
	border-radius: 8px
}
</style>
